import React, { useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { fetchKbArticles, searchKbArticles, deleteKbArticle, updateKbArticleStatus } from "../../Redux/Action/KbAction";
import LoadingOverlay from "react-loading-overlay-ts";
import { NavLink, useNavigate } from "react-router-dom";
import moment from "moment";
import Pagination from "../../Component/Pagination";

export default function KnowledgeBaseHub() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [domain, setDomain] = useState("ALL");
  const [page, setPage] = useState(1);
  const limit = 10;

  const { articles, totalItems, searchResults } = useSelector((state) => state.kb);
  const { admindetails } = useSelector((state) => state.SubAdmin);

  const loadArticles = () => {
    setLoading(true);
    dispatch(fetchKbArticles({ 
      page, 
      limit, 
      targetDomain: domain !== "ALL" ? domain : undefined 
    })).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadArticles();
  }, [page, domain]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) {
      loadArticles();
      return;
    }
    setLoading(true);
    dispatch(searchKbArticles({ q: search, domain }))
      .finally(() => setLoading(false));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this article?")) {
      setLoading(true);
      dispatch(deleteKbArticle(id)).then(() => {
        loadArticles();
      }).finally(() => setLoading(false));
    }
  };

  const handleStatusUpdate = (id, status) => {
    setLoading(true);
    dispatch(updateKbArticleStatus({ id, status })).then(() => {
      loadArticles();
    }).finally(() => setLoading(false));
  };

  const renderStatusBadge = (status) => {
    const colors = {
      PUBLISHED: "bg-emerald-100 text-emerald-800 border-emerald-200",
      DRAFT: "bg-amber-100 text-amber-800 border-amber-200",
      REJECTED: "bg-red-100 text-red-800 border-red-200"
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colors[status] || ""}`}>
        {status}
      </span>
    );
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} spinner text="Processing Knowledge Base...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            <div className="modern-user-section-card" style={{ background: "#fff", padding: 24, borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="mb-0" style={{ color: "#034328", fontWeight: 700 }}>Corporate Knowledge Base</h4>
                  <p className="text-muted small mb-0">Manage documentation for Business, Consumer, and Website ecosystems.</p>
                </div>
                <NavLink to="/knowledge-base/contribute" className="common-btn no-underline d-flex align-items-center">
                  <i className="fas fa-plus-circle me-2"></i> Contribute Article
                </NavLink>
              </div>

              {/* Search & Filters */}
              <form onSubmit={handleSearch} className="row g-3 mb-4 p-3 bg-light rounded-lg border border-gray-100">
                <div className="col-md-6">
                  <div className="input-group shadow-sm">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fas fa-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 focus-ring-none"
                      placeholder="Ask anything (e.g. 'How do I issue a refund?')..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <button type="submit" className="btn btn-emerald text-white px-4" style={{ background: "#034328" }}>
                      Search
                    </button>
                  </div>
                </div>
                <div className="col-md-3">
                  <select className="form-select shadow-sm" value={domain} onChange={(e) => setDomain(e.target.value)}>
                    <option value="ALL">All Domains</option>
                    <option value="BUSINESS">Business Ecosystem</option>
                    <option value="CONSUMER">Consumer Platform</option>
                    <option value="WEBSITE">Public Website</option>
                  </select>
                </div>
                {search.trim() && (
                   <div className="col-md-3 d-flex align-items-center">
                      <button type="button" className="btn btn-link text-muted" onClick={() => {setSearch(""); loadArticles();}}>Clear Search</button>
                   </div>
                )}
              </form>

              {/* Articles Table */}
              <div className="table-responsive">
                <table className="table table-hover align-middle border-top border-gray-100">
                  <thead className="bg-light">
                    <tr>
                      <th className="px-3">Article Title</th>
                      <th>Category</th>
                      <th>Domain</th>
                      <th>Status</th>
                      <th>Author</th>
                      <th>Last Updated</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {(search.trim() && searchResults.length > 0 ? searchResults : articles).map((article) => {
                      // Semantic search results might have different structure (metadata)
                      const item = article.metadata ? { 
                        _id: article.metadata.article_id,
                        title: article.metadata.title,
                        category: article.metadata.category,
                        targetDomain: article.metadata.targetDomain,
                        status: "PUBLISHED", 
                        authorId: { name: "Semantic Search" },
                        updatedAt: article.metadata.updatedAt
                      } : article;

                      if (!item._id) return null;

                      return (
                        <tr key={item._id} className="transition-all hover:bg-emerald-50/30">
                          <td className="px-3">
                            <div style={{ fontWeight: 600, color: "#111" }}>{item.title}</div>
                          </td>
                          <td className="text-muted small">{item.category}</td>
                          <td>
                            <span className="badge bg-light text-dark border">{item.targetDomain}</span>
                          </td>
                          <td>{renderStatusBadge(item.status)}</td>
                          <td className="small">{item.authorId?.name || "System"}</td>
                          <td className="small text-muted">{item.updatedAt ? moment(item.updatedAt).format("DD MMM YYYY") : "-"}</td>
                          <td>
                            <div className="d-flex justify-content-center gap-2">
                              {admindetails?.role === "Admin" && item.status === "DRAFT" && (
                                <button className="btn btn-sm btn-outline-success shadow-sm" onClick={() => handleStatusUpdate(item._id, "PUBLISHED")} title="Approve & Publish">
                                  <i className="fas fa-check"></i>
                                </button>
                              )}
                              <NavLink to={`/knowledge-base/edit/${item._id}`} className="btn btn-sm btn-outline-primary shadow-sm" title="Edit Article">
                                <i className="fas fa-pencil"></i>
                              </NavLink>
                              <button className="btn btn-sm btn-outline-danger shadow-sm" onClick={() => handleDelete(item._id)} title="Delete Article">
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {(search.trim() && searchResults.length === 0) && (
                      <tr>
                        <td colSpan="7" className="text-center py-5 text-muted">
                          <i className="fas fa-search-minus fa-3x mb-3 opacity-20"></i>
                          <p>No semantic matches found for "{search}". Try different keywords.</p>
                        </td>
                      </tr>
                    )}
                    {(!search.trim() && articles.length === 0) && (
                      <tr>
                        <td colSpan="7" className="text-center py-5 text-muted">
                          <p>No articles found. Start by contributing one!</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!search.trim() && articles.length > 0 && (
                <div className="mt-4 border-top pt-4">
                  <Pagination
                    handlePageClick={(e) => setPage(e.selected + 1)}
                    pageCount={Math.ceil(totalItems / limit)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </LoadingOverlay>
    </Layout>
  );
}
